import { useContext, useEffect, useState, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import '../../styles/page-components.css';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChatService from '../../app/services/hooks/useChatService';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';

interface ChatMessage {
    id: number;
    role: 'user' | 'assistant';
    role_display: string;
    content: string;
    sources: any[] | null;
    created_at: string;
}

interface Conversation {
    id: number;
    title: string;
    user_name: string;
    last_message: string;
    last_message_role: string;
    message_count: number;
    created_at: string;
    updated_at: string;
}

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Asistente de Ayuda', isCurrent: true }
];

const Chat = () => {
    const { auth } = useContext(AuthContext);
    const { 
        sendMessage: sendMessageApi,
        getConversations,
        getConversation,
        deleteConversation,
        fetchingSendMessage,
        fetchingGetConversations,
        fetchingGetConversation
    } = useChatService();

    // Estado
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [showSidebar, setShowSidebar] = useState(true);
    const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // Constantes para validación de archivos
    const MAX_FILE_SIZE_MB = 10;
    const ALLOWED_FILE_TYPES = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'text/plain'
    ];

    // Cargar conversaciones al inicio
    useEffect(() => {
        loadConversations();
    }, []);

    // Scroll al final cuando hay nuevos mensajes
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadConversations = () => {
        getConversations({}, {
            onSuccess: (response: ServiceResponse) => {
                setConversations(response.data.conversations || []);
            },
            onError: (response: ServiceResponse) => {
                console.error('Error loading conversations:', response.message);
            }
        });
    };

    const loadConversation = (conversationId: number) => {
        setCurrentConversationId(conversationId);
        getConversation(conversationId, {
            onSuccess: (response: ServiceResponse) => {
                setMessages(response.data.conversation?.messages || []);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                setCurrentConversationId(null);
            }
        });
    };

    const startNewConversation = () => {
        setCurrentConversationId(null);
        setMessages([]);
        setInputMessage('');
        setSelectedDocument(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        inputRef.current?.focus();
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tamaño
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            toast.error(`El archivo es demasiado grande. Tamaño máximo: ${MAX_FILE_SIZE_MB} MB`);
            e.target.value = '';
            return;
        }

        // Validar tipo
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            toast.error('Tipo de archivo no permitido. Formatos aceptados: PDF, DOCX, DOC, TXT');
            e.target.value = '';
            return;
        }

        setSelectedDocument(file);
    };

    const handleRemoveDocument = () => {
        setSelectedDocument(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSendMessage = () => {
        const trimmedMessage = inputMessage.trim();
        if (!trimmedMessage || fetchingSendMessage) return;

        // Preparar contenido del mensaje para mostrar
        let displayContent = trimmedMessage;
        if (selectedDocument) {
            displayContent = `📄 [${selectedDocument.name}]\n\n${trimmedMessage}`;
        }

        // Agregar mensaje del usuario inmediatamente
        const tempUserMessage: ChatMessage = {
            id: Date.now(),
            role: 'user',
            role_display: 'Usuario',
            content: displayContent,
            sources: null,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempUserMessage]);
        
        // Guardar referencia al documento antes de limpiar
        const documentToSend = selectedDocument;
        
        setInputMessage('');
        setSelectedDocument(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // Enviar al backend (con documento si existe)
        sendMessageApi(trimmedMessage, currentConversationId || undefined, documentToSend || undefined, {
            onSuccess: (response: ServiceResponse) => {
                const { conversation_id, assistant_message } = response.data;
                
                // Actualizar el ID de conversación si es nueva
                if (!currentConversationId && conversation_id) {
                    setCurrentConversationId(conversation_id);
                    loadConversations(); // Recargar lista de conversaciones
                }
                
                // Agregar respuesta del asistente
                if (assistant_message) {
                    setMessages(prev => [...prev, assistant_message]);
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al enviar mensaje');
                // Agregar mensaje de error como respuesta del asistente
                const errorMessage: ChatMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    role_display: 'Asistente',
                    content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta nuevamente.',
                    sources: null,
                    created_at: new Date().toISOString()
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleDeleteConversation = (conversationId: number) => {
        useReactConfirmAlert().requestConfirmation({
            title: '¿Eliminar conversación?',
            message: 'Esta acción no se puede deshacer.',
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteConversation(conversationId, {
                            onSuccess: () => {
                                if (currentConversationId === conversationId) {
                                    startNewConversation();
                                }
                                loadConversations();
                                toast.success('Conversación eliminada');
                            },
                            onError: (response: ServiceResponse) => {
                                toast.error(response.message);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => { return; }
                }
            }
        });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
            return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Ayer';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('es-CL', { weekday: 'long' });
        } else {
            return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
        }
    };

    return (
        <div className="container-fluid">
            <div className="page-container">
                <PageHeader
                    title=""
                    breadcrumbs={breadcrumbs}
                />

                <div className="row" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
                    {/* Sidebar con conversaciones */}
                    {showSidebar && (
                        <div className="col-md-3 pe-0" style={{ height: '100%' }}>
                            <DefaultCard>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0 fw-bold">Conversaciones</h6>
                                    <button 
                                        className="btn btn-sm btn-primary"
                                        onClick={startNewConversation}
                                        title="Nueva conversación"
                                    >
                                        <i className="fas fa-plus"></i>
                                    </button>
                                </div>
                                
                                <div style={{ overflowY: 'auto', maxHeight: 'calc(100% - 60px)' }}>
                                    {fetchingGetConversations ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                <span className="visually-hidden">Cargando...</span>
                                            </div>
                                        </div>
                                    ) : conversations.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fas fa-comments fa-2x mb-2"></i>
                                            <p className="small mb-0">No hay conversaciones aún</p>
                                        </div>
                                    ) : (
                                        conversations.map(conv => (
                                            <div
                                                key={conv.id}
                                                className={`p-2 mb-1 rounded cursor-pointer ${currentConversationId === conv.id ? 'bg-primary bg-opacity-10 border-start border-3 border-primary' : 'hover-bg-light'}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => loadConversation(conv.id)}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1 overflow-hidden">
                                                        <p className="mb-0 fw-semibold text-truncate" style={{ fontSize: '0.9rem' }}>
                                                            {conv.title || 'Conversación sin título'}
                                                        </p>
                                                        {conv.last_message && (
                                                            <p className="mb-0 text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                                                                {conv.last_message}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-column align-items-end ms-2">
                                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                            {formatDate(conv.updated_at)}
                                                        </small>
                                                        <button
                                                            className="btn btn-link btn-sm p-0 text-danger"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.id); }}
                                                            title="Eliminar"
                                                        >
                                                            <i className="fas fa-trash" style={{ fontSize: '0.7rem' }}></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </DefaultCard>
                        </div>
                    )}

                    {/* Área de chat */}
                    <div className={showSidebar ? 'col-md-9 ps-2' : 'col-12'} style={{ height: '100%' }}>
                        <DefaultCard>
                            {/* Header del chat */}
                            <div className="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
                                <div className="d-flex align-items-center">
                                    <button 
                                        className="btn btn-link p-0 me-3 d-md-none"
                                        onClick={() => setShowSidebar(!showSidebar)}
                                    >
                                        <i className="fas fa-bars"></i>
                                    </button>
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-2"
                                            style={{ width: '40px', height: '40px' }}
                                        >
                                            <i className="fas fa-robot text-white"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Asistente Monere</h6>
                                            <small className="text-muted">
                                                {fetchingSendMessage ? 'Escribiendo...' : 'En línea'}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={startNewConversation}
                                >
                                    <i className="fas fa-plus me-1"></i>
                                    Nueva conversación
                                </button>
                            </div>

                            {/* Mensajes */}
                            <div 
                                className="flex-grow-1 overflow-auto mb-3"
                                style={{ maxHeight: 'calc(100% - 140px)' }}
                            >
                                {messages.length === 0 ? (
                                    <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center p-4">
                                        <div 
                                            className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mb-4"
                                            style={{ width: '80px', height: '80px' }}
                                        >
                                            <i className="fas fa-robot fa-2x text-primary"></i>
                                        </div>
                                        <h5 className="mb-2">¡Hola! Soy el Asistente de Monere</h5>
                                        <p className="text-muted mb-4" style={{ maxWidth: '500px' }}>
                                            Puedo ayudarte respondiendo preguntas basándome en los documentos 
                                            de la plataforma. También puedo <strong>analizar tus documentos legales</strong> como 
                                            abogado chileno.
                                        </p>
                                        
                                        {/* Funcionalidades */}
                                        <div className="row g-3 mb-4" style={{ maxWidth: '600px' }}>
                                            <div className="col-6">
                                                <div className="p-3 rounded-3 h-100" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                                                    <i className="fas fa-comments text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                                                    <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Preguntas</h6>
                                                    <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                                                        Consulta sobre nuestros servicios legales
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-3 rounded-3 h-100" style={{ background: '#fef3c7', border: '1px solid #fcd34d' }}>
                                                    <i className="fas fa-file-alt text-warning mb-2" style={{ fontSize: '1.5rem' }}></i>
                                                    <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Análisis de documentos</h6>
                                                    <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                                                        Sube un contrato para análisis legal
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>Prueba con estas sugerencias:</p>
                                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                                            {['¿Cómo registro una marca?', '¿Cuáles son los plazos?', 'Analiza mi contrato'].map((suggestion, i) => (
                                                <button
                                                    key={i}
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => {
                                                        if (suggestion === 'Analiza mi contrato') {
                                                            fileInputRef.current?.click();
                                                            setInputMessage('Por favor, analiza este documento y dame tu opinión legal');
                                                        } else {
                                                            setInputMessage(suggestion);
                                                        }
                                                    }}
                                                >
                                                    {suggestion === 'Analiza mi contrato' && <i className="fas fa-paperclip me-1"></i>}
                                                    {suggestion}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-2">
                                        {messages.map((msg, index) => (
                                            <div
                                                key={msg.id || index}
                                                className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                                            >
                                                {msg.role === 'assistant' && (
                                                    <div 
                                                        className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                                        style={{ width: '32px', height: '32px' }}
                                                    >
                                                        <i className="fas fa-robot text-white" style={{ fontSize: '0.8rem' }}></i>
                                                    </div>
                                                )}
                                                <div
                                                    className={`p-3 rounded-3 ${msg.role === 'user' 
                                                        ? 'bg-primary text-white' 
                                                        : 'bg-light'}`}
                                                    style={{ 
                                                        maxWidth: '75%',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    <p className="mb-1">{msg.content}</p>
                                                    <small className={msg.role === 'user' ? 'text-white-50' : 'text-muted'} style={{ fontSize: '0.7rem' }}>
                                                        {new Date(msg.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                                                    </small>
                                                </div>
                                                {msg.role === 'user' && (
                                                    <div 
                                                        className="rounded-circle bg-secondary d-flex align-items-center justify-content-center ms-2 flex-shrink-0"
                                                        style={{ width: '32px', height: '32px' }}
                                                    >
                                                        <i className="fas fa-user text-white" style={{ fontSize: '0.8rem' }}></i>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        
                                        {/* Indicador de escritura */}
                                        {fetchingSendMessage && (
                                            <div className="d-flex justify-content-start mb-3">
                                                <div 
                                                    className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-2"
                                                    style={{ width: '32px', height: '32px' }}
                                                >
                                                    <i className="fas fa-robot text-white" style={{ fontSize: '0.8rem' }}></i>
                                                </div>
                                                <div className="bg-light p-3 rounded-3">
                                                    <div className="typing-indicator">
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </div>

                            {/* Input de mensaje */}
                            <div className="border-top pt-3">
                                {/* Documento seleccionado */}
                                {selectedDocument && (
                                    <div 
                                        className="mb-3 p-3 rounded-3 d-flex align-items-center justify-content-between"
                                        style={{ 
                                            background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)',
                                            border: '1px solid #93c5fd'
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="rounded-2 bg-primary d-flex align-items-center justify-content-center me-3"
                                                style={{ width: '40px', height: '40px' }}
                                            >
                                                <i className="fas fa-file-alt text-white"></i>
                                            </div>
                                            <div>
                                                <p className="mb-0 fw-semibold text-truncate" style={{ maxWidth: '300px', color: '#1e40af' }}>
                                                    {selectedDocument.name}
                                                </p>
                                                <small className="text-muted">
                                                    {(selectedDocument.size / 1024 / 1024).toFixed(2)} MB • 
                                                    <span className="text-primary ms-1">Listo para análisis legal</span>
                                                </small>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-link text-danger p-0"
                                            onClick={handleRemoveDocument}
                                            title="Quitar documento"
                                        >
                                            <i className="fas fa-times-circle fa-lg"></i>
                                        </button>
                                    </div>
                                )}

                                <div className="d-flex gap-2">
                                    {/* Input oculto para archivo */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="d-none"
                                        accept=".pdf,.doc,.docx,.txt"
                                        onChange={handleFileSelect}
                                    />
                                    
                                    {/* Botón adjuntar documento */}
                                    <button
                                        type="button"
                                        className={`btn ${selectedDocument ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={fetchingSendMessage}
                                        title="Adjuntar documento para análisis legal"
                                        style={{ minWidth: '45px' }}
                                    >
                                        <i className="fas fa-paperclip"></i>
                                    </button>
                                    
                                    <textarea
                                        ref={inputRef}
                                        className="form-control"
                                        placeholder={selectedDocument 
                                            ? "Escribe qué quieres que analice del documento..." 
                                            : "Escribe tu mensaje..."
                                        }
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        rows={1}
                                        style={{ resize: 'none', maxHeight: '100px' }}
                                        disabled={fetchingSendMessage}
                                    />
                                    <button
                                        className="btn btn-primary px-4"
                                        onClick={handleSendMessage}
                                        disabled={!inputMessage.trim() || fetchingSendMessage}
                                    >
                                        {fetchingSendMessage ? (
                                            <span className="spinner-border spinner-border-sm" role="status">
                                                <span className="visually-hidden">Enviando...</span>
                                            </span>
                                        ) : (
                                            <i className="fas fa-paper-plane"></i>
                                        )}
                                    </button>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle me-1"></i>
                                        {selectedDocument 
                                            ? 'El documento será analizado como abogado chileno (no se guarda en la base de conocimiento)'
                                            : 'Presiona Enter para enviar, Shift+Enter para nueva línea'
                                        }
                                    </small>
                                    {!selectedDocument && (
                                        <small className="text-muted">
                                            <i className="fas fa-paperclip me-1"></i>
                                            PDF, DOCX, DOC, TXT (máx. 10MB)
                                        </small>
                                    )}
                                </div>
                            </div>
                        </DefaultCard>
                    </div>
                </div>

                {/* Estilos para el indicador de escritura */}
                <style>{`
                    .typing-indicator {
                        display: flex;
                        gap: 4px;
                    }
                    .typing-indicator span {
                        width: 8px;
                        height: 8px;
                        background-color: #6c757d;
                        border-radius: 50%;
                        animation: typing 1.4s infinite ease-in-out both;
                    }
                    .typing-indicator span:nth-child(1) {
                        animation-delay: -0.32s;
                    }
                    .typing-indicator span:nth-child(2) {
                        animation-delay: -0.16s;
                    }
                    @keyframes typing {
                        0%, 80%, 100% {
                            transform: scale(0);
                        }
                        40% {
                            transform: scale(1);
                        }
                    }
                    .hover-bg-light:hover {
                        background-color: #f8f9fa;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default Chat;

